import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useContest } from '../../contexts/ContestContext';
import Blocks from '../blocks/Blocks';
import Button from '../buttons/Button';
import StyledButton from '../buttons/StyledButton';
import HelpModal from '../modals/HelpModal';

import landingHeaderImg from '../../../img/coorslive/landing_header.jpg';
import landingPrizesImg from '../../../img/coorslive/landing_prizes.png';
import styles from '../../../styles/pages/coorslive-landing.module.css';

interface CoorsliveLandingPageProps {
    page: LandingPage;
}

const CoorsliveLandingPage = ({ page }: CoorsliveLandingPageProps) => {
    const { locale } = useIntl();
    const { contests = [], blocks = [] } = page || {};
    const { started: contestStarted = false } = useContest();

    const [modalOpened, setModalOpened] = useState(false);

    const closeModal = useCallback(() => setModalOpened(false), [setModalOpened]);

    const { url: retailContestUrl = null } = useMemo(
        () => contests.find(({ handle = null }) => handle?.endsWith('_retail')),
        [contests],
    );
    const { url: opContestUrl = null } = useMemo(
        () => contests.find(({ handle = null }) => handle?.endsWith('_op')),
        [contests],
    );
    const { url: noPinUrl } = useMemo(
        () => contests.find(({ handle = null }) => handle?.endsWith('_nopin')),
        [contests],
    );

    return (
        <div className={styles.container}>
            <img src={landingHeaderImg} className={styles.headerImg} alt="" />
            <div className={styles.inner}>
                {contestStarted ? (
                    <>
                        <div className={classNames(styles.card, styles.main)}>
                            <h2>
                                <FormattedMessage
                                    defaultMessage="Welcome to Coors Live!"
                                    description="Coors Live landing"
                                />
                            </h2>

                            <div className={classNames(styles.card, styles.prizes)}>
                                <img src={landingPrizesImg} alt="Coors Live Prizes" />
                                <span>
                                    <FormattedMessage
                                        defaultMessage="The hub for a chance to win* music prizes."
                                        description="Coors Live landing"
                                    />
                                </span>
                            </div>

                            <h3>
                                <FormattedMessage
                                    defaultMessage="Where did you get your PIN?"
                                    description="Coors Live landing"
                                />
                            </h3>

                            <StyledButton
                                className={styles.styledButton}
                                href={retailContestUrl}
                                direct
                            >
                                <FormattedMessage
                                    defaultMessage="In a case"
                                    description="Coors Live landing"
                                />
                            </StyledButton>

                            <StyledButton
                                className={styles.styledButton}
                                href={opContestUrl}
                                direct
                            >
                                <FormattedMessage
                                    defaultMessage="With a pint"
                                    description="Coors Live landing"
                                />
                            </StyledButton>

                            <Button className={styles.pinHelp} onClick={() => setModalOpened(true)}>
                                <FormattedMessage
                                    defaultMessage="How do I get a PIN?"
                                    description="Coors Live landing"
                                />
                            </Button>
                        </div>

                        <Blocks className={styles.blocks} blocks={blocks} isContainer />
                    </>
                ) : (
                    <p className={styles.comeBackLater}>
                        <FormattedMessage
                            defaultMessage="Stay tuned! Coors Live will be back this summer."
                            description="Coors Live landing"
                        />
                    </p>
                )}
            </div>

            {modalOpened ? (
                <HelpModal onClose={closeModal}>
                    <div className={styles.helpDialogContent}>
                        <h2 className={styles.title}>
                            <FormattedMessage
                                defaultMessage="Unlock Coors Live's full potential"
                                description="Coors Live Landing"
                            />
                        </h2>

                        <p>
                            <FormattedMessage
                                defaultMessage="With a unique PIN, you could win* exclusive music experiences and prizes every day of summer!"
                                description="Coors Live Landing"
                            />
                        </p>

                        <div className={styles.splitSection}>
                            <div>
                                <h3>
                                    <FormattedMessage
                                        defaultMessage="At participating stores"
                                        description="Coors Live Landing"
                                    />
                                </h3>
                                <p>
                                    <FormattedMessage
                                        defaultMessage="Buy a select pack of Coors Light and find a unique PIN inside. "
                                        description="Coors Live Landing"
                                    />
                                </p>
                            </div>
                            <div>
                                <h3>
                                    <FormattedMessage
                                        defaultMessage="At participating bars/restaurants"
                                        description="Coors Live Landing"
                                    />
                                </h3>
                                <p>
                                    <FormattedMessage
                                        defaultMessage="Buy a Coors Light and ask your Server for details"
                                        description="Coors Live Landing"
                                    />
                                </p>
                            </div>
                        </div>

                        <StyledButton
                            className={classNames(styles.button, styles.closeButton)}
                            onClick={closeModal}
                        >
                            <FormattedMessage
                                defaultMessage="Got it"
                                description="Coors Live Landing"
                            />
                        </StyledButton>

                        <StyledButton
                            className={classNames(styles.button, styles.playAnywayButton)}
                            href={noPinUrl}
                            direct
                        >
                            <FormattedMessage
                                defaultMessage="Play Coors Live Games"
                                description="Coors Live Landing"
                            />
                        </StyledButton>

                        <p className={styles.finePrint}>
                            <span>
                                <FormattedMessage
                                    defaultMessage="NO PURCHASE NECESSARY"
                                    description="Coors Live Landing"
                                />
                            </span>
                            <br></br>
                            <span>
                                &#x2a;
                                <FormattedMessage
                                    defaultMessage="Speak with your Server or click here"
                                    description="Coors Live Landing"
                                />
                                &nbsp;-&nbsp;
                                <a
                                    href={locale === 'fr' ? '/fr/reglements' : '/en/rules'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FormattedMessage
                                        defaultMessage="Coors Live Official Contest Rules and Regulations"
                                        description="Coors Live Landing"
                                    />
                                </a>
                            </span>
                        </p>
                    </div>
                </HelpModal>
            ) : null}
        </div>
    );
};

export default CoorsliveLandingPage;
